import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";
import { twentyFourHoursInMs } from "../../../../utils";

const EMPTY_LAYERS = [];

/**
 * Fetch a list of layers to add to the map
 * @param {Array} swapVisibility layer ids whose default visibility is flipped
 * @param {function} [applyOverrides] optional callback that receives the
 * default layers and returns layers with overrides applied (e.g. from the URL).
 * Only used the first time layers are loaded
 */
const useLayers = (swapVisibility = [], applyOverrides) => {
  const [layers, setLayers] = useState([]);
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    getAccessTokenSilently,
  } = useAuth0();

  // the response differs by role, so the cache must be keyed on auth status
  // and the request must wait for Auth0 to resolve
  const { data, isError, isLoading } = useQuery(
    ["Layers", isAuthenticated],
    async () => {
      let headers = {};
      if (isAuthenticated) {
        const token = await getAccessTokenSilently();
        headers = { Authorization: `Bearer ${token}` };
      }
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_ENDPOINT}/api/public-map/layers`,
          { headers }
        );
        return data;
      } catch (err) {
        console.error(err);
      }
    },
    {
      enabled: !isAuthLoading,
      refetchOnWindowFocus: false,
      staleTime: twentyFourHoursInMs,
      cacheTime: twentyFourHoursInMs,
    }
  );

  // sorted by draw order (this order also drives the layers control list),
  // copies are made so the react-query cache is never mutated
  const defaultLayers = useMemo(
    () =>
      data
        ? [...data].sort((a, b) =>
            (a.drawOrder || 0) > (b.drawOrder || 0) ? -1 : 1
          )
        : EMPTY_LAYERS,
    [data]
  );

  useEffect(() => {
    if (!data) return;
    const swappedLayers = defaultLayers.map((layer) => {
      if (!swapVisibility.includes(layer.id)) return layer;
      return {
        ...layer,
        layout: {
          ...layer.layout,
          visibility:
            layer.layout?.visibility === "visible" ? "none" : "visible",
        },
      };
    });
    setLayers((prevLayers) =>
      // keep user changes if the layers were already initialized
      prevLayers.length
        ? prevLayers
        : applyOverrides
        ? applyOverrides(swappedLayers)
        : swappedLayers
    );
  }, [defaultLayers]); //eslint-disable-line

  return {
    isLoading,
    isError,
    layers,
    setLayers,
    defaultLayers,
  };
};

export default useLayers;
